import ButtonNormal from "@/components/form/ButtonNormal";
import { FadeIn } from "@/lib/css-lib";
import React, { useContext, useState } from "react";
import styled from "styled-components";

/** モーダルダイアログコンテキスト */
export interface ModalDialogContext {
    /** 開閉状態 */
    isOpen: boolean;
    /** 開く */
    onChange: (isOpen: boolean, option?: ModalDialogOption, callback?: DialogCallback) => void;
 }

export const ModalDialogContext = React.createContext<ModalDialogContext>({
    isOpen: false,
    onChange: () => null,
})

/** モーダルダイアログのオプション */
export interface ModalDialogOption {
    /** タイトル */
    title?: string
    /** メッセージ */
    message?: string | React.ComponentType<any> | React.ReactElement
    /** モーダルの内容 */
    contents?: React.ComponentType<{ onCloseCallback: (result: DialogResult) => any }>
}

type DialogResult = 'ok' | 'cancel';

type DialogCallback = (res: DialogResult) => void;

/**
 * モーダル結果
 */
export interface ModalDialogResult {
    /** 結果 */
    dialogResult: DialogResult;
}

/**
 * のモーダルダイアログ操作メソッドを取得します。
 * @param option 
 */
export const useModalDialog = () => {
    const context = useContext(ModalDialogContext);
    
    return {
        /** ダイアログが開いている状態かどうかを返す。 */
        isOpen: context.isOpen,
        /** 開く */
        open: (option: ModalDialogOption) => {
            return new Promise<ModalDialogResult>((resolve) => {
                const resultCallback = (result: DialogResult) => {
                    resolve({
                        dialogResult: result
                    })
                }
                context.onChange(true, option, resultCallback);
            })
        },

        close: (dialogResult: 'ok' | 'cancel') => {
            context.onChange(false);
        }
    }
}


export const ModalDialogProvider: React.FC = ({children}) => {
    const [isOpen, setOpen] = useState(false);
    const [option, setOption] = useState<ModalDialogOption>();
    const [closeCallback, setCloseCallback] = useState<{callback: DialogCallback}>();

    const onClosed = (dialogResult: DialogResult) => {
        setOpen(false);
        if (closeCallback?.callback) {
            closeCallback.callback(dialogResult);
        }
    };

    const onChange = (isOpen: boolean, option?: ModalDialogOption, callback?: DialogCallback) => {
        setOpen(isOpen);
        if (option) {
            setOption(option);
        }
        if (callback) {
            setCloseCallback({ callback })
        }
    }

    const context: ModalDialogContext = {
        isOpen,
        onChange,
    }

    const CustomContents = option?.contents;

    return <div>
        <ModalDialogContext.Provider value={context}>
            {children}
        </ModalDialogContext.Provider>
        {
            (isOpen && option) &&
                <FadeIn speed="0.2s">
                    <Overlay>
                        { 
                            CustomContents ? <CustomContents onCloseCallback={e => onClosed(e)} /> :
                            <Content>
                                {
                                    option.title && 
                                    <ModalHead>
                                        {option.title}
                                    </ModalHead>
                                }
                                <ModalMain>{option.message}</ModalMain>
                                <ButtonNormal onClick={() => onClosed('ok')}>閉じる</ButtonNormal>
                            </Content>
                        }
                    </Overlay>
                </FadeIn>
        }
    </div>
}


const Overlay = styled.div`
    position: fixed;
    z-index: 10000;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;


const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 30px;
    border: 1px solid #ccc;
    background-color: #ffffff;
    max-width: 80%;
    padding: 40px;

    & > * {
      margin: 12px 0;  
    }

    & > button {
      width: 360px;  
    }
`;

const ModalHead = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    font-size: x-large;
    font-weight: bold;
`;

const ModalMain = styled.div`
    padding: 10px;
`;
